import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api, getApiErrorMessage } from '../lib/api'
import {
  csvImportHeadersLookUsable,
  CUSTOMER_CSV_HEADERS,
  downloadUtf8BomFile,
  excelCsvPhoneCell,
  mapCustomerRowFromCsv,
  parseCsv,
  readCustomerImportFileAsText,
  rowCellsToMap,
  stringifyCsvRow,
} from '../lib/customerCsv'
import {
  formatBirthYmdInput,
  formatKoreanPhoneInput,
  normalizeBirthDateForApi,
  normalizeBirthDateForCsvImport,
  restoreLeadingZeroKoreanPhoneDigits,
} from '../lib/crmInputFormat'
import { patchCreateFormFromExtracted } from '../lib/customerBusinessCardExtract'
import { segmentOptionsForStored, sourceOptionsForStored } from '../lib/customerCrmOptions'
import MenuGuideButton from '../components/MenuGuideButton'
import { Button } from '../components/ui/button'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

const CRM_HEADER_BTN =
  'inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-sm font-bold shadow-sm'
const CRM_HEADER_BTN_SIZE = 'h-auto rounded-lg py-1.5 px-3 text-sm font-bold'

function pad2(n) {
  return String(n).padStart(2, '0')
}

/** @returns {{ startLocal: string, endLocal: string }} datetime-local 값 */
function defaultScheduleRangeLocal() {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  start.setHours(start.getHours() + 1)
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  const toLocal = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  return { startLocal: toLocal(start), endLocal: toLocal(end) }
}

function toDatetimeLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function defaultContactAtLocal() {
  const now = new Date()
  now.setSeconds(0, 0)
  return toDatetimeLocal(now)
}

function customerRefQuery(id) {
  if (id == null) return ''
  return `customerRefId=${encodeURIComponent(String(id))}`
}

function formatYmd(v) {
  if (v == null || v === '') return ''
  const s = String(v).trim()
  return s.length >= 10 ? s.slice(0, 10) : s
}

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete(data) {
      onComplete?.(data)
    },
  }).open()
}

function formatDaumCustomerAddress(data) {
  const zone = String(data?.zonecode || '').trim()
  const road = String(data?.roadAddress || data?.autoRoadAddress || '').trim()
  const jibun = String(data?.jibunAddress || data?.autoJibunAddress || data?.address || '').trim()
  return [zone ? `(${zone})` : '', road || jibun].filter(Boolean).join(' ').trim()
}

function RequiredMark() {
  return <span className="text-rose-600" aria-hidden="true">*</span>
}

function optionDisplayLabel(options, value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const found = (Array.isArray(options) ? options : []).find((opt) => String(opt.value || '') === raw)
  return found?.label || raw
}

function compactCustomerDate(value) {
  const formatted = formatYmd(value)
  return formatted || ''
}

function customerSortHeadline(row, sortOption) {
  const sortKey = String(sortOption || '')
  if (sortKey.startsWith('lastContactAt')) {
    const value = compactCustomerDate(row?.lastContactAtDisplay || row?.lastContactAt)
    return value ? `최근 연락 ${value}` : ''
  }
  if (sortKey.startsWith('latestContractDate')) {
    const value = compactCustomerDate(row?.latestContractDate)
    return value ? `최근 계약 ${value}` : ''
  }
  if (sortKey.startsWith('nextMaturityDate')) {
    const value = compactCustomerDate(row?.nextMaturityDate)
    return value ? `다음 만기 ${value}` : ''
  }
  if (sortKey.startsWith('createdAt')) {
    const value = compactCustomerDate(row?.createdAtDisplay || row?.createdAt)
    return value ? `등록 ${value}` : ''
  }
  const value = compactCustomerDate(row?.lastTouchAtDisplay || row?.lastTouchAt || row?.updatedAtDisplay || row?.updatedAt)
  return value ? `마지막 활동 ${value}` : ''
}

const CUSTOMER_SORT_OPTIONS = [
  { value: 'lastTouchAt,DESC', label: '마지막 활동순' },
  { value: 'lastContactAt,DESC', label: '최근 연락순' },
  { value: 'latestContractDate,DESC', label: '최근 계약순' },
  { value: 'nextMaturityDate,ASC', label: '만기 임박순' },
  { value: 'createdAt,DESC', label: '최근 등록순' },
  { value: 'name,ASC', label: '가나다순' },
]

export default function CustomerManagementPage() {
  const navigate = useNavigate()
  const { hasAnyRole } = useAuth()
  const isOperator = hasAnyRole([Roles.OPERATOR])
  const [list, setList] = useState([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [sortOption, setSortOption] = useState('lastTouchAt,DESC')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createPhone, setCreatePhone] = useState('')
  const [createBirthDate, setCreateBirthDate] = useState('')
  const [createRegion, setCreateRegion] = useState('')
  const [createCustomerSegment, setCreateCustomerSegment] = useState('')
  const [createAffiliation, setCreateAffiliation] = useState('')
  const [createHonorific, setCreateHonorific] = useState('')
  const [createHomeAddress, setCreateHomeAddress] = useState('')
  const [createCompanyAddress, setCreateCompanyAddress] = useState('')
  const [createCustomerEmail, setCreateCustomerEmail] = useState('')
  const [createCustomerSource, setCreateCustomerSource] = useState('')
  const [createMemo, setCreateMemo] = useState('')
  const [createBusy, setCreateBusy] = useState(false)

  const [createBizWizardOpen, setCreateBizWizardOpen] = useState(false)
  const [createBizWizardStep, setCreateBizWizardStep] = useState('front')
  const [createBizDraftFrontId, setCreateBizDraftFrontId] = useState('')
  const [createBizDraftBackId, setCreateBizDraftBackId] = useState('')
  const [createBizWizardBusy, setCreateBizWizardBusy] = useState(false)
  const [createBizExtracting, setCreateBizExtracting] = useState(false)
  const [createPendingBizFrontId, setCreatePendingBizFrontId] = useState('')
  const [createPendingBizBackId, setCreatePendingBizBackId] = useState('')

  const createSegmentSelectOptions = useMemo(
    () => segmentOptionsForStored(createCustomerSegment),
    [createCustomerSegment],
  )
  const createSourceSelectOptions = useMemo(
    () => sourceOptionsForStored(createCustomerSource),
    [createCustomerSource],
  )

  const csvFileInputRef = useRef(null)
  const [csvExportBusy, setCsvExportBusy] = useState(false)
  const [csvImportBusy, setCsvImportBusy] = useState(false)

  const [memoModal, setMemoModal] = useState(null)
  const [memoDraft, setMemoDraft] = useState('')
  const [memoLoading, setMemoLoading] = useState(false)
  const [memoSaving, setMemoSaving] = useState(false)

  const [schedModal, setSchedModal] = useState(null)
  const [schedTitle, setSchedTitle] = useState('')
  const [schedDesc, setSchedDesc] = useState('')
  const [schedStart, setSchedStart] = useState('')
  const [schedEnd, setSchedEnd] = useState('')
  const [schedBusy, setSchedBusy] = useState(false)

  const [contactModal, setContactModal] = useState(null)
  const [contactAt, setContactAt] = useState('')
  const [contactContent, setContactContent] = useState('')
  const [contactBusy, setContactBusy] = useState(false)
  const [inlineSavingKey, setInlineSavingKey] = useState('')
  const [taskSelectSeq, setTaskSelectSeq] = useState({})

  const bumpTaskSelectReset = useCallback((customerId) => {
    if (customerId == null) return
    setTaskSelectSeq((prev) => ({ ...prev, [customerId]: (prev[customerId] || 0) + 1 }))
  }, [])

  const loadList = useCallback(async (p = 0) => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/customers', { params: { page: p, size: 20, sort: sortOption } })
      const data = unwrap(res) || {}
      setList(Array.isArray(data.items) ? data.items : [])
      setPage(typeof data.page === 'number' ? data.page : p)
      setPageSize(typeof data.size === 'number' && data.size > 0 ? data.size : 20)
      setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
      setTotalElements(typeof data.totalElements === 'number' ? data.totalElements : 0)
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '목록을 불러오지 못했습니다.')
      setList([])
      setTotalElements(0)
    } finally {
      setLoading(false)
    }
  }, [sortOption])

  useEffect(() => {
    loadList(0)
  }, [loadList])

  useEffect(() => {
    if (createOpen) return
    setCreateBizWizardOpen(false)
    setCreateBizWizardStep('front')
    setCreateBizDraftFrontId('')
    setCreateBizDraftBackId('')
    setCreateBizExtracting(false)
    setCreatePendingBizFrontId('')
    setCreatePendingBizBackId('')
  }, [createOpen])

  const openCreateBizWizard = () => {
    setCreateBizWizardOpen(true)
    setCreateBizWizardStep('front')
    setCreateBizDraftFrontId('')
    setCreateBizDraftBackId('')
    setCreateBizExtracting(false)
    setErr('')
  }

  const uploadCreateBizCardFile = async (file) => {
    const form = new FormData()
    form.append('file', file)
    const up = await api.post('/files', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      __silent: true,
    })
    const payload = up?.data?.data ?? up?.data ?? {}
    const fid = payload.fileId || payload.id
    if (!fid) throw new Error('no_file_id')
    return fid
  }

  const runCreateBizCardExtractAndFill = async () => {
    if (!createBizDraftFrontId) {
      try { window?.toast?.show?.('명함 앞면 이미지를 업로드해 주세요.', 'error') } catch { }
      return
    }
    setCreateBizExtracting(true)
    setCreateBizWizardBusy(true)
    setErr('')
    try {
      const res = await api.post(
        '/customer-management/customers/extract-business-card-preview',
        {
          frontFileId: createBizDraftFrontId,
          backFileId: createBizDraftBackId || null,
        },
        { __silent: true },
      )
      const data = unwrap(res) || {}
      const patch = patchCreateFormFromExtracted(data)
      if (patch.customerName) setCreateName(patch.customerName)
      if (patch.customerPhoneDigits) setCreatePhone(formatKoreanPhoneInput(patch.customerPhoneDigits))
      if (patch.birthDateDigits) setCreateBirthDate(formatBirthYmdInput(patch.birthDateDigits))
      if (patch.region != null) setCreateRegion(patch.region)
      if (patch.customerSegment != null) setCreateCustomerSegment(patch.customerSegment)
      if (patch.affiliation != null) setCreateAffiliation(patch.affiliation)
      if (patch.honorific != null) setCreateHonorific(patch.honorific)
      if (patch.homeAddress != null) setCreateHomeAddress(patch.homeAddress)
      if (patch.companyAddress != null) setCreateCompanyAddress(patch.companyAddress)
      if (patch.customerEmail != null) setCreateCustomerEmail(patch.customerEmail)
      if (patch.customerSource != null) setCreateCustomerSource(patch.customerSource)
      setCreatePendingBizFrontId(createBizDraftFrontId)
      setCreatePendingBizBackId(createBizDraftBackId || '')
      setCreateBizWizardOpen(false)
      setCreateBizWizardStep('front')
      setCreateBizDraftFrontId('')
      setCreateBizDraftBackId('')
      try {
        window?.toast?.show?.('명함에서 정보를 읽어 폼에 반영했습니다. 확인 후 등록해 주세요.', 'success')
      } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '명함 인식에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
    } finally {
      setCreateBizExtracting(false)
      setCreateBizWizardBusy(false)
    }
  }

  const openMemoModal = async (row) => {
    if (!row?.id) return
    setMemoModal({ id: row.id, name: row.customerName || '(이름 없음)' })
    setMemoDraft('')
    setMemoLoading(true)
    setErr('')
    try {
      const res = await api.get(`/customer-management/customers/${row.id}/summary`, { __silent: true })
      const data = unwrap(res)
      setMemoDraft(data?.memo != null ? String(data.memo) : '')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '고객 정보를 불러오지 못했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
      setMemoModal(null)
    } finally {
      setMemoLoading(false)
    }
  }

  const saveMemoFromList = async () => {
    if (!memoModal?.id) return
    setMemoSaving(true)
    setErr('')
    try {
      await api.patch(`/customer-management/customers/${memoModal.id}/memo`, { memo: memoDraft }, { __silent: true })
      try { window?.toast?.show?.('메모가 저장되었습니다. 고객 메모는 내부 관리용이며 고객에게 알림이 가지 않습니다.', 'success') } catch { }
      setMemoModal(null)
      await loadList(page)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '메모 저장에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setMemoSaving(false)
    }
  }

  const openScheduleModal = (row) => {
    if (!row?.id) return
    const { startLocal, endLocal } = defaultScheduleRangeLocal()
    setSchedModal({ id: row.id, name: row.customerName || '(이름 없음)' })
    setSchedTitle(`[고객] ${row.customerName || '고객'}`)
    setSchedDesc('')
    setSchedStart(startLocal)
    setSchedEnd(endLocal)
  }

  const submitSchedule = async () => {
    if (!schedModal?.id) return
    const title = (schedTitle || '').trim()
    if (!title) {
      try { window?.toast?.show?.('일정 제목을 입력해 주세요.', 'error') } catch { }
      return
    }
    let startAt
    let endAt
    try {
      startAt = new Date(schedStart).toISOString()
      endAt = new Date(schedEnd).toISOString()
    } catch {
      try { window?.toast?.show?.('시작·종료 시각을 확인해 주세요.', 'error') } catch { }
      return
    }
    if (!schedStart || !schedEnd || new Date(endAt) <= new Date(startAt)) {
      try { window?.toast?.show?.('종료 시각은 시작보다 이후여야 합니다.', 'error') } catch { }
      return
    }
    setSchedBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title,
          description: (schedDesc || '').trim() || null,
          startAt,
          endAt,
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: true,
          reminderOffsetsMinutes: null,
          rrule: null,
          customerRefId: schedModal.id,
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('개인 일정으로 등록되었습니다. 고객에게 별도 알림은 전송되지 않습니다.', 'success') } catch { }
      setSchedModal(null)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '일정 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSchedBusy(false)
    }
  }

  const openContactModal = (row) => {
    if (!row?.id) return
    setContactModal({ id: row.id, name: row.customerName || '(이름 없음)' })
    setContactAt(defaultContactAtLocal())
    setContactContent('')
  }

  const runCustomerTaskAction = (row, action) => {
    if (!row?.id || !action) return
    const q = customerRefQuery(row.id)
    bumpTaskSelectReset(row.id)
    if (action === 'medical') {
      navigate(`/health-analysis?${q}`)
    } else if (action === 'hidden-insurance') {
      navigate(`/hidden-insurance-money?${q}`)
    } else if (action === 'claims') {
      navigate(`/support/claims?${q}`)
    } else if (action === 'fire') {
      navigate(`/fire/building-registry?${q}`)
    } else if (action === 'schedule') {
      openScheduleModal(row)
    } else if (action === 'contact') {
      openContactModal(row)
    } else if (action === 'memo') {
      openMemoModal(row)
    }
  }

  const submitContact = async () => {
    if (!contactModal?.id) return
    const content = (contactContent || '').trim()
    if (!content) {
      try { window?.toast?.show?.('연락 내용을 입력해 주세요.', 'error') } catch { }
      return
    }
    let start
    try {
      start = new Date(contactAt)
      if (!contactAt || Number.isNaN(start.getTime())) throw new Error('invalid_date')
    } catch {
      try { window?.toast?.show?.('연락 일시를 확인해 주세요.', 'error') } catch { }
      return
    }
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    setContactBusy(true)
    setErr('')
    try {
      await api.post(
        '/calendar/events/personal',
        {
          title: `[연락] ${contactModal.name || '고객'}`,
          description: content,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          allDay: false,
          timeZone: 'Asia/Seoul',
          useDefaultReminders: false,
          reminderOffsetsMinutes: [],
          rrule: null,
          customerRefId: contactModal.id,
          personalCategory: 'OTHER',
        },
        { __silent: true },
      )
      try { window?.toast?.show?.('연락 이력이 등록되었습니다. 일정 메뉴에서도 확인할 수 있습니다.', 'success') } catch { }
      setContactModal(null)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '연락 이력 등록에 실패했습니다.'
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setContactBusy(false)
    }
  }

  const saveListProfileField = async (row, field, value) => {
    if (!row?.id || !field) return
    let normalizedValue = value
    if (field === 'birthDate') {
      const norm = normalizeBirthDateForApi(value)
      if (!norm.ok) {
        try { window?.toast?.show?.(norm.message, 'error') } catch { }
        return
      }
      normalizedValue = norm.value
    }
    const before = row[field] == null ? '' : String(row[field])
    const after = normalizedValue == null ? '' : String(normalizedValue)
    if (before === after) return
    const key = `${row.id}:${field}`
    setInlineSavingKey(key)
    setErr('')
    try {
      const res = await api.patch(
        `/customer-management/customers/${row.id}`,
        { [field]: normalizedValue },
        { __silent: true },
      )
      const saved = unwrap(res) || {}
      setList((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...saved } : item)))
      try { window?.toast?.show?.('고객 정보를 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '고객 정보 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setInlineSavingKey('')
    }
  }

  const saveListMemoField = async (row, value) => {
    if (!row?.id) return
    const before = row.memo != null ? String(row.memo).trim() : ''
    const after = String(value || '').trim()
    if (before === after) return
    const key = `${row.id}:memo`
    setInlineSavingKey(key)
    setErr('')
    try {
      await api.patch(
        `/customer-management/customers/${row.id}/memo`,
        { memo: after || null },
        { __silent: true },
      )
      setList((prev) => prev.map((item) => (item.id === row.id ? { ...item, memo: after || null } : item)))
      try { window?.toast?.show?.('메모를 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '메모 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setInlineSavingKey('')
    }
  }

  const openCreateAddressSearch = (target) => {
    if (createBusy) return
    try {
      openDaumPostcode((data) => {
        const address = formatDaumCustomerAddress(data)
        if (!address) return
        if (target === 'company') setCreateCompanyAddress(address)
        else setCreateHomeAddress(address)
      })
    } catch {
      try { window?.toast?.show?.('주소검색(카카오 우편번호) 스크립트를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.', 'error', 5000) } catch { }
    }
  }

  const submitCreate = async () => {
    const name = (createName || '').trim()
    const phone = (createPhone || '').replace(/\D/g, '')
    if (!name) {
      try { window?.toast?.show?.('이름을 입력해 주세요.', 'error') } catch { }
      return
    }
    if (!(phone.length === 10 || phone.length === 11)) {
      try { window?.toast?.show?.('연락처는 숫자 10~11자리로 입력해 주세요.', 'error') } catch { }
      return
    }
    const birthNorm = normalizeBirthDateForApi(createBirthDate)
    if (!birthNorm.ok) {
      try { window?.toast?.show?.(birthNorm.message, 'error') } catch { }
      return
    }
    setCreateBusy(true)
    setErr('')
    try {
      const res = await api.post('/customer-management/customers', {
        customerName: name,
        customerPhone: phone,
        birthDate: birthNorm.value,
        region: (createRegion || '').trim() || null,
        memo: (createMemo || '').trim() || null,
        customerSegment: (createCustomerSegment || '').trim(),
        affiliation: (createAffiliation || '').trim(),
        honorific: (createHonorific || '').trim(),
        homeAddress: (createHomeAddress || '').trim(),
        companyAddress: (createCompanyAddress || '').trim(),
        customerEmail: (createCustomerEmail || '').trim() || null,
        customerSource: (createCustomerSource || '').trim(),
      }, { __silent: true })
      const data = unwrap(res)
      const newId = data?.id
      if (newId != null && (createPendingBizFrontId || createPendingBizBackId)) {
        try {
          if (createPendingBizFrontId) {
            await api.post(
              `/customer-management/customers/${newId}/business-cards`,
              { fileId: createPendingBizFrontId, side: 'FRONT' },
              { __silent: true },
            )
          }
          if (createPendingBizBackId) {
            await api.post(
              `/customer-management/customers/${newId}/business-cards`,
              { fileId: createPendingBizBackId, side: 'BACK' },
              { __silent: true },
            )
          }
        } catch (eBiz) {
          const m = getApiErrorMessage(eBiz, '명함 이미지 연결에 실패했습니다.')
          try { window?.toast?.show?.(m, 'error', 4500) } catch { }
        }
      }
      const existingCustomer = data?.created === false || data?.existing === true
      try {
        window?.toast?.show?.(
          existingCustomer ? '이미 등록된 고객입니다. 기존 고객 상세로 이동합니다.' : '고객이 등록되었습니다.',
          'success',
        )
      } catch { }
      setCreateName('')
      setCreatePhone('')
      setCreateBirthDate('')
      setCreateRegion('')
      setCreateCustomerSegment('')
      setCreateAffiliation('')
      setCreateHonorific('')
      setCreateHomeAddress('')
      setCreateCompanyAddress('')
      setCreateCustomerEmail('')
      setCreateCustomerSource('')
      setCreateMemo('')
      setCreatePendingBizFrontId('')
      setCreatePendingBizBackId('')
      setCreateOpen(false)
      if (newId != null) {
        navigate(`/support/customers/${newId}`)
      } else {
        await loadList(page)
      }
    } catch (e) {
      const msg = getApiErrorMessage(e, '등록에 실패했습니다.')
      setErr(msg)
      if (e?.response?.status !== 409) {
        try { window?.toast?.show?.(msg, 'error', 5000) } catch { }
      }
    } finally {
      setCreateBusy(false)
    }
  }

  const fetchAllCustomerRows = useCallback(async () => {
    const size = 100
    let pageIdx = 0
    let totalPages = 1
    const all = []
    while (pageIdx < totalPages) {
      const res = await api.get('/customer-management/customers', { params: { page: pageIdx, size, sort: sortOption } })
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items : []
      all.push(...items)
      totalPages = typeof data.totalPages === 'number' ? data.totalPages : 0
      if (items.length === 0) break
      pageIdx += 1
    }
    return all
  }, [sortOption])

  const downloadCsvExport = async () => {
    setCsvExportBusy(true)
    setErr('')
    try {
      const rows = await fetchAllCustomerRows()
      const headerLine = stringifyCsvRow(CUSTOMER_CSV_HEADERS)
      const lines = [headerLine]
      for (const row of rows) {
        const bd = row.birthDate
        const birthDisp =
          bd == null || bd === ''
            ? ''
            : String(bd).length >= 10
              ? String(bd).slice(0, 10)
              : String(bd)
        const cells = [
          row.customerName ?? '',
          excelCsvPhoneCell(row.customerPhone),
          row.customerSegment ?? '',
          birthDisp,
          row.region ?? '',
          row.affiliation ?? '',
          row.honorific ?? '',
          row.homeAddress ?? '',
          row.companyAddress ?? '',
          row.customerEmail ?? '',
          row.customerSource ?? '',
          row.memo ?? '',
        ]
        lines.push(stringifyCsvRow(cells))
      }
      const fname = `고객목록_${new Date().toISOString().slice(0, 10)}.csv`
      downloadUtf8BomFile(fname, lines.join('\r\n'))
      try {
        window?.toast?.show?.(`CSV를 저장했습니다. (${rows.length}명)`, 'success')
      } catch { }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'CSV 저장에 실패했습니다.'
      setErr(msg)
      try {
        window?.toast?.show?.(msg, 'error')
      } catch { }
    } finally {
      setCsvExportBusy(false)
    }
  }

  const importCustomerCsvRow = async (parsed) => {
    const digits = restoreLeadingZeroKoreanPhoneDigits(parsed.customerPhone)
    if (!(digits.length === 10 || digits.length === 11)) {
      throw new Error('연락처는 숫자 10~11자리로 입력해 주세요.')
    }
    const birthNorm = normalizeBirthDateForCsvImport(parsed.birthDateRaw)
    if (!birthNorm.ok) {
      throw new Error(birthNorm.message)
    }
    const nameTrim = (parsed.customerName || '').trim()
    if (!nameTrim) {
      throw new Error('이름을 입력해 주세요.')
    }

    const resLookup = await api.get('/customer-management/customers/lookup-by-phone', {
      params: { customerPhone: digits },
      __silent: true,
    })
    const lu = unwrap(resLookup) || {}
    const registered = Boolean(lu.registered)
    const customerRefId = lu.customerRefId

    const profilePayload = {
      customerName: nameTrim || null,
      birthDate: birthNorm.value,
      customerPhone: digits,
      region: (parsed.region || '').trim(),
      customerSegment: (parsed.customerSegment || '').trim(),
      affiliation: (parsed.affiliation || '').trim(),
      honorific: (parsed.honorific || '').trim(),
      homeAddress: (parsed.homeAddress || '').trim(),
      companyAddress: (parsed.companyAddress || '').trim(),
      customerEmail: (parsed.customerEmail || '').trim() || null,
      customerSource: (parsed.customerSource || '').trim(),
    }
    const memoTrim = (parsed.memo || '').trim()

    if (registered && customerRefId != null) {
      await api.patch(`/customer-management/customers/${customerRefId}`, profilePayload, { __silent: true })
      await api.patch(
        `/customer-management/customers/${customerRefId}/memo`,
        { memo: memoTrim || null },
        { __silent: true },
      )
      return 'updated'
    }

    await api.post(
      '/customer-management/customers',
      {
        customerName: nameTrim,
        customerPhone: digits,
        birthDate: birthNorm.value,
        region: (parsed.region || '').trim() || null,
        memo: memoTrim || null,
        customerSegment: (parsed.customerSegment || '').trim(),
        affiliation: (parsed.affiliation || '').trim(),
        honorific: (parsed.honorific || '').trim(),
        homeAddress: (parsed.homeAddress || '').trim(),
        companyAddress: (parsed.companyAddress || '').trim(),
        customerEmail: (parsed.customerEmail || '').trim() || null,
        customerSource: (parsed.customerSource || '').trim(),
      },
      { __silent: true },
    )
    return 'created'
  }

  const onCsvFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setCsvImportBusy(true)
    setErr('')
    let created = 0
    let updated = 0
    const rowErrors = []
    try {
      const text = await readCustomerImportFileAsText(file)
      const { headers, rows: dataRows } = parseCsv(text)
      if (dataRows.length === 0) {
        throw new Error(
          '가져올 데이터 행이 없습니다. 첫 줄은 헤더(이름, 전화번호, 고객 구분, …)로 두어 주세요.',
        )
      }
      if (!csvImportHeadersLookUsable(headers)) {
        throw new Error(
          '첫 줄에서 필수 열(이름, 전화번호 또는 연락처)을 찾지 못했습니다. 엑셀에서는 **다른 이름으로 저장 → CSV UTF-8(쉼표로 분리)(*.csv)** 로 저장했는지 확인해 주세요. (일반 CSV는 한글이 깨져 열이 맞지 않을 수 있습니다.)',
        )
      }
      const lineBase = 2
      for (let i = 0; i < dataRows.length; i++) {
        const rowMap = rowCellsToMap(headers, dataRows[i])
        const parsed = mapCustomerRowFromCsv(rowMap)
        const rowNum = lineBase + i
        try {
          const outcome = await importCustomerCsvRow(parsed)
          if (outcome === 'created') created += 1
          else if (outcome === 'updated') updated += 1
        } catch (rowErr) {
          const msg = rowErr?.response?.data?.message || rowErr?.message || '처리 실패'
          rowErrors.push(`${rowNum}행: ${msg}`)
        }
      }
      const parts = []
      if (created) parts.push(`신규 ${created}명`)
      if (updated) parts.push(`갱신 ${updated}명`)
      if (rowErrors.length === 0) {
        try {
          window?.toast?.show?.(parts.length ? `가져오기 완료 (${parts.join(', ')})` : '처리할 유효한 데이터가 없었습니다.', 'success')
        } catch { }
      } else {
        const anyOk = created > 0 || updated > 0
        try {
          window?.toast?.show?.(
            anyOk
              ? `가져오기 ${parts.join(', ')} · 실패 ${rowErrors.length}건(빨간 안내 박스에 사유)`
              : `가져오기 실패 ${rowErrors.length}건(빨간 안내 박스에 행별 사유)`,
            'error',
          )
        } catch { }
        const allBlocked = !anyOk
        setErr(
          (allBlocked
            ? '아래 행은 서버 요청 전에 막혔습니다(네트워크 탭에 호출이 없을 수 있음). 연락처·이름·생년월일을 확인해 주세요.\n\n'
            : '일부 행만 실패했습니다.\n\n') + rowErrors.join('\n'),
        )
      }
      await loadList(page)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'CSV 가져오기에 실패했습니다.'
      setErr(msg)
      try {
        window?.toast?.show?.(msg, 'error')
      } catch { }
    } finally {
      setCsvImportBusy(false)
    }
  }

  return (
    <div className="py-2">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">고객관리</h1>
            <MenuGuideButton slug="support-customers" title="고객관리 가이드" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <Link
            to="/support/customers/work-queue"
            className={`${CRM_HEADER_BTN} border-teal-200 bg-teal-50 text-teal-900 hover:bg-teal-100`}
          >
            고객업무 허브
          </Link>
          <Link
            to="/support/maturity-ledger"
            className={`${CRM_HEADER_BTN} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
          >
            보험만기대장
          </Link>
          {isOperator && (
            <Link
              to="/support/customer-registration"
              title="범위 선택·보험사·동의 등 단계별 진행 (운영자)"
              className={`${CRM_HEADER_BTN} border-sky-200 bg-gradient-to-b from-sky-50 to-white text-sky-900 hover:border-sky-400 hover:shadow`}
            >
              고객등록
            </Link>
          )}
          <Button
            type="button"
            variant="sgez"
            size="sm"
            className={CRM_HEADER_BTN_SIZE}
            onClick={() => setCreateOpen(true)}
          >
            고객 등록
          </Button>
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            className={CRM_HEADER_BTN_SIZE}
            disabled={csvExportBusy || csvImportBusy}
            onClick={() => downloadCsvExport()}
          >
            {csvExportBusy ? 'CSV 저장 중…' : 'CSV 내려받기'}
          </Button>
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            className={CRM_HEADER_BTN_SIZE}
            disabled={csvExportBusy || csvImportBusy}
            onClick={() => csvFileInputRef.current?.click()}
          >
            {csvImportBusy ? 'CSV 가져오는 중…' : 'CSV 올리기'}
          </Button>
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            aria-hidden
            onChange={onCsvFileChange}
          />
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            className={CRM_HEADER_BTN_SIZE}
            onClick={() => loadList(page)}
          >
            새로고침
          </Button>
        </div>
      </div>

      {err ? (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-white">
          <ErrorState
            description={err}
            onAction={() => loadList(page)}
            className="py-6"
          />
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-100 px-3 py-3 sm:flex-row sm:items-center sm:gap-x-3 sm:gap-y-2 sm:px-4">
          <div className="text-sm font-semibold text-slate-800">
            고객 목록
            <span className="ml-2 text-xs font-medium text-slate-500">전체 {totalElements.toLocaleString()}명</span>
          </div>
          <label className="flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-600 sm:w-auto">
            <span className="shrink-0">정렬</span>
            <select
              value={sortOption}
              aria-label="고객 목록 정렬"
              className="h-8 min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky sm:flex-none"
              onChange={(e) => {
                const next = e.target.value
                setSortOption(next)
              }}
            >
              {CUSTOMER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="md:overflow-x-auto md:overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">불러오는 중…</div>
          ) : list.length === 0 ? (
            <EmptyState
              title="등록된 고객이 없습니다"
              description="고객 등록으로 직접 추가하거나, 설문 응답과 진료정보분석을 통해 고객 정보를 연결할 수 있습니다."
              action={(
                <Button type="button" variant="sgez" size="sm" onClick={() => setCreateOpen(true)}>
                  고객 등록
                </Button>
              )}
            />
          ) : (
            <div className="min-w-0">
              <div className="space-y-3 bg-slate-50/80 p-3 md:hidden">
                {list.map((row) => {
                  const memoRaw = row.memo != null && String(row.memo).trim() !== '' ? String(row.memo).trim() : ''
                  const phone = row.customerPhone ? formatKoreanPhoneInput(row.customerPhone) : ''
                  const birth = formatYmd(row.birthDate)
                  const segmentLabel = optionDisplayLabel(segmentOptionsForStored(row.customerSegment), row.customerSegment)
                  const sourceLabel = optionDisplayLabel(sourceOptionsForStored(row.customerSource), row.customerSource)
                  const headline = customerSortHeadline(row, sortOption)
                  const meta = [phone || '연락처 없음', birth, row.region].filter(Boolean)
                  return (
                    <article
                      key={`mobile-customer-${row.id}`}
                      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm ring-1 ring-slate-100"
                    >
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => navigate(`/support/customers/${row.id}`)}
                        >
                          <div className="truncate text-sm font-bold text-slate-900">
                            {row.customerName || '(이름 없음)'}
                          </div>
                          {meta.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] leading-5 text-slate-500">
                              {meta.map((item) => (
                                <span key={item} className="max-w-full truncate">{item}</span>
                              ))}
                            </div>
                          ) : null}
                        </button>
                        <Link
                          to={`/support/customers/${row.id}`}
                          className="shrink-0 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-brand-navy shadow-sm hover:bg-slate-50"
                        >
                          상세
                        </Link>
                      </div>
                      {(segmentLabel || sourceLabel || headline) ? (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {segmentLabel ? (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
                              {segmentLabel}
                            </span>
                          ) : null}
                          {sourceLabel ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                              {sourceLabel}
                            </span>
                          ) : null}
                          {headline ? (
                            <span className="min-w-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                              {headline}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {memoRaw ? (
                        <div className="mt-2 line-clamp-2 rounded border border-slate-100 bg-slate-50 px-2.5 py-2 text-xs leading-5 text-slate-600">
                          {memoRaw}
                        </div>
                      ) : null}
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => openContactModal(row)}
                        >
                          연락 기록
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => openScheduleModal(row)}
                        >
                          일정
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => openMemoModal(row)}
                        >
                          메모
                        </button>
                      </div>
                      <select
                        key={`mobile-list-task-${row.id}-${taskSelectSeq[row.id] || 0}`}
                        defaultValue=""
                        aria-label={`${row.customerName || '고객'} 업무 선택`}
                        className="mt-2 h-9 w-full rounded border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
                        onChange={(e) => runCustomerTaskAction(row, e.target.value)}
                      >
                        <option value="">업무 선택</option>
                        <option value="medical">진료정보분석</option>
                        <option value="hidden-insurance">숨은보험금</option>
                        <option value="claims">보험금청구</option>
                        <option value="fire">화재설계</option>
                      </select>
                    </article>
                  )
                })}
              </div>
              <div className="hidden min-w-0 md:block">
              <table className="w-full max-w-full table-fixed border-collapse text-left text-sm">
                <colgroup>
                  <col className="w-[4%]" />
                  <col className="w-[12%]" />
                  <col className="w-[10%]" />
                  <col className="w-[11%]" />
                  <col className="w-[9%]" />
                  <col className="w-[9%]" />
                  <col className="w-[26%]" />
                  <col className="w-[10%]" />
                  <col className="w-[9%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/95 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-2 px-1 text-center leading-tight">no</th>
                    <th className="py-2 px-1.5 leading-tight">고객명</th>
                    <th className="py-2 px-1.5 leading-tight">전화</th>
                    <th className="py-2 px-1.5 leading-tight whitespace-normal">생년월일<br /><span className="font-normal normal-case text-[10px] text-slate-400">(상령일)</span></th>
                    <th className="py-2 px-1.5 leading-tight">구분</th>
                    <th className="py-2 px-1.5 leading-tight">출처</th>
                    <th className="py-2 px-1.5 leading-tight">메모</th>
                    <th className="py-2 px-1 text-center leading-tight">업무</th>
                    <th className="py-2 px-1 text-center leading-tight">상세</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((row, idx) => {
                    const absoluteIndex = page * pageSize + idx
                    const fallbackTotal = page * pageSize + list.length
                    const rowNo = Math.max((totalElements || fallbackTotal) - absoluteIndex, 1)
                    const memoRaw = row.memo != null && String(row.memo).trim() !== '' ? String(row.memo) : ''
                    const phone = row.customerPhone ? formatKoreanPhoneInput(row.customerPhone) : ''
                    const birth = formatYmd(row.birthDate)
                    const insuranceAgeDate = formatYmd(row.insuranceAgeDate)
                    const q = customerRefQuery(row.id)
                    return (
                      <tr key={row.id} className="hover:bg-slate-50/90 align-middle">
                        <td className="py-2 px-1 text-center text-[11px] tabular-nums text-slate-500">{rowNo}</td>
                        <td className="min-w-0 px-1.5 py-2">
                          <div className="truncate text-xs font-medium text-slate-900" title={row.customerName || '(이름 없음)'}>
                            {row.customerName || '(이름 없음)'}
                          </div>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-[11px] text-slate-700 tabular-nums break-all">
                          {phone || '—'}
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-[11px] text-slate-700 tabular-nums">
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={birth}
                            placeholder="YYYY-MM-DD"
                            disabled={inlineSavingKey === `${row.id}:birthDate`}
                            className="w-full min-w-0 rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-50"
                            onBlur={(e) => saveListProfileField(row, 'birthDate', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                          />
                          <div className="mt-0.5 truncate text-[10px] text-slate-500" title={insuranceAgeDate ? `상령일 ${insuranceAgeDate}` : undefined}>
                            상령일 {insuranceAgeDate || '—'}
                          </div>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-xs text-slate-700">
                          <select
                            value={row.customerSegment || ''}
                            disabled={inlineSavingKey === `${row.id}:customerSegment`}
                            className="w-full min-w-0 rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-50"
                            onChange={(e) => saveListProfileField(row, 'customerSegment', e.target.value)}
                          >
                            {segmentOptionsForStored(row.customerSegment).map((opt) => (
                              <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="min-w-0 px-1.5 py-2 text-xs text-slate-700">
                          <select
                            value={row.customerSource || ''}
                            disabled={inlineSavingKey === `${row.id}:customerSource`}
                            className="w-full min-w-0 rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-50"
                            onChange={(e) => saveListProfileField(row, 'customerSource', e.target.value)}
                          >
                            {sourceOptionsForStored(row.customerSource).map((opt) => (
                              <option key={opt.value || '__empty'} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="min-w-0 px-1.5 py-2">
                          <textarea
                            key={`memo-${row.id}-${memoRaw}`}
                            defaultValue={memoRaw}
                            rows={1}
                            maxLength={4000}
                            disabled={inlineSavingKey === `${row.id}:memo`}
                            placeholder="상담 메모 등"
                            className="h-8 w-full min-w-0 resize-none overflow-hidden rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] leading-6 text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky disabled:bg-slate-50"
                            onBlur={(e) => saveListMemoField(row, e.target.value)}
                          />
                        </td>
                        <td className="min-w-0 px-1 py-2">
                          <select
                            key={`list-task-${row.id}-${taskSelectSeq[row.id] || 0}`}
                            defaultValue=""
                            aria-label="업무"
                            title="업무 선택"
                            className="w-full min-w-0 cursor-pointer rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
                            onChange={(e) => {
                              const action = e.target.value
                              if (!action) return
                              bumpTaskSelectReset(row.id)
                              if (action === 'medical') {
                                navigate(`/health-analysis?${q}`)
                              } else if (action === 'hidden-insurance') {
                                navigate(`/hidden-insurance-money?${q}`)
                              } else if (action === 'claims') {
                                navigate(`/support/claims?${q}`)
                              } else if (action === 'fire') {
                                navigate(`/fire/building-registry?${q}`)
                              } else if (action === 'schedule') {
                                openScheduleModal(row)
                              } else if (action === 'contact') {
                                openContactModal(row)
                              } else if (action === 'memo') {
                                openMemoModal(row)
                              }
                            }}
                          >
                            <option value="">업무</option>
                            <option value="medical">진료</option>
                            <option value="hidden-insurance">숨은보험금</option>
                            <option value="claims">보험금청구</option>
                            <option value="fire">화재</option>
                            <option value="schedule">일정</option>
                            <option value="contact">연락</option>
                            <option value="memo">메모</option>
                          </select>
                        </td>
                        <td className="px-1 py-2 text-center align-middle">
                          <Link
                            to={`/support/customers/${row.id}`}
                            className="inline-block py-0.5 text-xs font-medium text-brand-navy underline decoration-slate-200 underline-offset-4 hover:decoration-brand-navy"
                          >
                            상세
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-600">
            <button
              type="button"
              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={page <= 0 || loading}
              onClick={() => {
                const np = Math.max(0, page - 1)
                loadList(np)
              }}
            >
              이전
            </button>
            <span>
              {page + 1} / {totalPages || 1}
            </span>
            <button
              type="button"
              className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={loading || page + 1 >= totalPages}
              onClick={() => loadList(page + 1)}
            >
              다음
            </button>
          </div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-[58]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={createBusy}
            onClick={() => { if (!createBusy) setCreateOpen(false) }}
            aria-label="고객 등록 닫기"
          />
          <div className="relative mx-auto max-w-6xl px-3 py-8 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto flex max-h-[min(92vh,760px)] flex-col overflow-hidden rounded-2xl border bg-white shadow-xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-create-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-create-title" className="font-bold text-sm sm:text-base">고객 등록</div>
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  disabled={createBusy}
                  onClick={() => setCreateOpen(false)}
                >
                  닫기
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 text-sm">
                <p className="mb-4 text-xs text-slate-500">
                  동일 연락처 고객이 이미 있으면 기존 고객 상세로 이동합니다. 고객 등록만으로 고객에게 알림이 전송되지는 않습니다.
                </p>
                <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                      <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                      <div className="text-sm font-bold text-slate-900">기본 정보</div>
                    </div>
                    <div className="space-y-3 p-4">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-600">이름 <RequiredMark /></span>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm"
                          placeholder="이름"
                          value={createName}
                          onChange={(e) => setCreateName(e.target.value)}
                          disabled={createBusy}
                          required
                          aria-required="true"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-600">연락처 <RequiredMark /></span>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm tabular-nums"
                          placeholder="010-0000-0000 (하이픈 자동)"
                          value={createPhone}
                          onChange={(e) => setCreatePhone(formatKoreanPhoneInput(e.target.value))}
                          disabled={createBusy}
                          inputMode="numeric"
                          autoComplete="tel"
                          required
                          aria-required="true"
                        />
                      </label>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm tabular-nums"
                        placeholder="YYYY-MM-DD (하이픈 자동)"
                        value={createBirthDate}
                        onChange={(e) => setCreateBirthDate(formatBirthYmdInput(e.target.value))}
                        disabled={createBusy}
                        inputMode="numeric"
                        autoComplete="bday"
                      />
                      <p className="text-[11px] text-slate-500">
                        생년월일을 넣으면 상령일(생일 + 6개월)이 자동 계산되어 캘린더 알림에 사용됩니다.
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-0.5 block text-xs font-medium text-slate-600">고객 구분 (선택)</label>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createCustomerSegment}
                            onChange={(e) => setCreateCustomerSegment(e.target.value)}
                            disabled={createBusy}
                          >
                            {createSegmentSelectOptions.map((o) => (
                              <option key={o.value === '' ? '_seg' : o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs font-medium text-slate-600">고객 출처 (선택)</label>
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                            value={createCustomerSource}
                            onChange={(e) => setCreateCustomerSource(e.target.value)}
                            disabled={createBusy}
                          >
                            {createSourceSelectOptions.map((o) => (
                              <option key={o.value === '' ? '_src' : o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="지역 (선택, 시·도 등)"
                        value={createRegion}
                        onChange={(e) => setCreateRegion(e.target.value)}
                        maxLength={120}
                        disabled={createBusy}
                      />
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="소속 (선택)"
                        value={createAffiliation}
                        onChange={(e) => setCreateAffiliation(e.target.value)}
                        maxLength={255}
                        disabled={createBusy}
                      />
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="호칭 (선택)"
                        value={createHonorific}
                        onChange={(e) => setCreateHonorific(e.target.value)}
                        maxLength={80}
                        disabled={createBusy}
                      />
                      <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-slate-600">집 주소 (선택)</label>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={createBusy}
                            onClick={() => openCreateAddressSearch('home')}
                          >
                            주소 검색
                          </button>
                        </div>
                        <textarea
                          className="w-full min-h-[72px] rounded border px-3 py-2 text-sm"
                          placeholder="집 주소"
                          value={createHomeAddress}
                          onChange={(e) => setCreateHomeAddress(e.target.value)}
                          maxLength={500}
                          disabled={createBusy}
                        />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-slate-600">회사 주소 (선택)</label>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            disabled={createBusy}
                            onClick={() => openCreateAddressSearch('company')}
                          >
                            주소 검색
                          </button>
                        </div>
                        <textarea
                          className="w-full min-h-[72px] rounded border px-3 py-2 text-sm"
                          placeholder="회사 주소"
                          value={createCompanyAddress}
                          onChange={(e) => setCreateCompanyAddress(e.target.value)}
                          maxLength={500}
                          disabled={createBusy}
                        />
                      </div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="이메일 (선택)"
                        type="email"
                        value={createCustomerEmail}
                        onChange={(e) => setCreateCustomerEmail(e.target.value)}
                        maxLength={255}
                        autoComplete="email"
                        disabled={createBusy}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                        <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                        <div className="text-sm font-bold text-slate-900">메모</div>
                      </div>
                      <div className="p-4">
                        <div className="rounded-md border border-amber-200 bg-amber-50/90 px-2.5 py-1.5 text-[10px] leading-snug text-amber-950">
                          주민번호·계좌·카드·건강 진단 등 민감정보는 적지 마세요.
                        </div>
                        <textarea
                          className="mt-1.5 w-full min-h-[120px] rounded border px-3 py-2 text-sm"
                          placeholder="상담 메모 등"
                          value={createMemo}
                          onChange={(e) => setCreateMemo(e.target.value)}
                          maxLength={4000}
                          disabled={createBusy}
                        />
                        <div className="mt-0.5 text-[10px] text-slate-500">{createMemo.length} / 4000</div>
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                          <div className="text-sm font-bold text-slate-900">명함</div>
                        </div>
                        <Button
                          type="button"
                          variant="sgezOutline"
                          size="sm"
                          className="shrink-0"
                          disabled={createBusy}
                          onClick={openCreateBizWizard}
                        >
                          명함 업로드
                        </Button>
                      </div>
                      <div className="p-4">
                        {createPendingBizFrontId ? (
                          <p className="text-xs text-emerald-800">등록 시 명함 이미지가 새 고객에 연결됩니다.</p>
                        ) : (
                          <p className="text-xs text-slate-500">명함 업로드 시 명함 정보가 기본 정보 필드로 자동 입력됩니다.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/90 px-4 py-3">
                      <span className="h-5 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
                      <div className="text-sm font-bold text-slate-900">등록</div>
                    </div>
                    <div className="space-y-4 p-4">
                      <p className="text-xs leading-relaxed text-slate-600">
                        등록 후 고객 상세에서 다음 활동 추천, AI 고객 메시지, 계약·진료 분석 등 업무를 이어갈 수 있습니다.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="sgezOutline"
                          size="sm"
                          className="flex-1"
                          disabled={createBusy}
                          onClick={() => setCreateOpen(false)}
                        >
                          취소
                        </Button>
                        <Button
                          type="button"
                          variant="sgez"
                          size="sm"
                          className="flex-1"
                          disabled={createBusy}
                          onClick={submitCreate}
                        >
                          {createBusy ? '등록 중…' : '등록'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen && createBizWizardOpen ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={createBizWizardBusy}
            onClick={() => { if (!createBizWizardBusy) setCreateBizWizardOpen(false) }}
            aria-label="명함 업로드 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto relative rounded-2xl bg-white shadow-xl border overflow-hidden max-h-[min(90vh,640px)] flex flex-col"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5 shrink-0">
                <div className="font-bold text-sm sm:text-base">
                  {createBizWizardStep === 'front' ? '명함 앞면 업로드' : '명함 뒷면 업로드'}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={createBizWizardBusy}
                  onClick={() => setCreateBizWizardOpen(false)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 text-sm">
                {createBizWizardStep === 'front' ? (
                  <>
                    <p className="text-xs text-slate-500">
                      앞면을 올린 뒤「다음」에서 뒷면을 추가하거나, 뒷면 없이 바로 인식할 수 있습니다.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={createBizWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCreateBizWizardBusy(true)
                        try {
                          const fid = await uploadCreateBizCardFile(f)
                          setCreateBizDraftFrontId(fid)
                          try { window?.toast?.show?.('앞면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCreateBizWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {createBizDraftFrontId ? (
                      <p className="text-[11px] text-emerald-700">앞면 파일이 준비되었습니다.</p>
                    ) : null}
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                        disabled={createBizWizardBusy}
                        onClick={() => setCreateBizWizardOpen(false)}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                        disabled={createBizWizardBusy || !createBizDraftFrontId}
                        onClick={() => setCreateBizWizardStep('back')}
                      >
                        다음
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">뒷면이 없으면 건너뛰고 인식만 진행할 수 있습니다.</p>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={createBizWizardBusy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        setCreateBizWizardBusy(true)
                        try {
                          const fid = await uploadCreateBizCardFile(f)
                          setCreateBizDraftBackId(fid)
                          try { window?.toast?.show?.('뒷면이 업로드되었습니다.', 'success') } catch { }
                        } catch (upErr) {
                          const um = getApiErrorMessage(upErr, '업로드에 실패했습니다.')
                          setErr(um)
                          try { window?.toast?.show?.(um, 'error', 4000) } catch { }
                        } finally {
                          setCreateBizWizardBusy(false)
                        }
                      }}
                      className="block w-full text-xs"
                    />
                    {createBizDraftBackId ? (
                      <p className="text-[11px] text-emerald-700">뒷면 파일이 준비되었습니다.</p>
                    ) : null}
                    <div className="flex justify-between gap-2 pt-2">
                      <button
                        type="button"
                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                        disabled={createBizWizardBusy}
                        onClick={() => setCreateBizWizardStep('front')}
                      >
                        이전
                      </button>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                          disabled={createBizWizardBusy}
                          onClick={runCreateBizCardExtractAndFill}
                        >
                          뒷면 없이 완료
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                          disabled={createBizWizardBusy}
                          onClick={runCreateBizCardExtractAndFill}
                        >
                          완료
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {(createBizWizardBusy || createBizExtracting) ? (
                <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-white/80 px-4 backdrop-blur-[1px]">
                  <span
                    className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-slate-200 border-t-brand-navy"
                    aria-hidden
                  />
                  <p className="mt-3 max-w-[17rem] text-center text-sm font-medium text-slate-800">
                    {createBizExtracting ? '명함에서 정보를 읽는 중입니다. 잠시만 기다려 주세요.' : '처리 중입니다…'}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {memoModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={memoSaving}
            onClick={() => { if (!memoSaving) setMemoModal(null) }}
            aria-label="메모 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden flex max-h-[min(90vh,620px)] flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-memo-list-title"
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-memo-list-title" className="font-bold text-sm sm:text-base">
                  고객 메모 · {memoModal.name}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={memoSaving}
                  onClick={() => setMemoModal(null)}
                >
                  닫기
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 text-sm">
                {memoLoading ? (
                  <p className="text-sm text-slate-500">불러오는 중…</p>
                ) : (
                  <>
                    <div className="rounded-md border border-amber-200 bg-amber-50/90 px-3 py-2 text-[11px] leading-snug text-amber-950">
                      주민등록번호, 계좌번호, 카드번호, 건강·진단 상세 등 <strong>민감한 개인정보는 이 메모에 적지 마세요.</strong> 분실·유출 시 법적 책임이 발생할 수 있습니다.
                    </div>
                    <textarea
                      className="w-full min-h-[140px] rounded border border-slate-300 px-3 py-2 text-sm"
                      value={memoDraft}
                      onChange={(e) => setMemoDraft(e.target.value)}
                      maxLength={4000}
                      placeholder="상담 메모 등 (민감정보 제외)"
                      disabled={memoSaving}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                      <span>{memoDraft.length} / 4000</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                          disabled={memoSaving}
                          onClick={() => setMemoModal(null)}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                          disabled={memoSaving}
                          onClick={saveMemoFromList}
                        >
                          {memoSaving ? '저장 중…' : '저장'}
                        </button>
                      </div>
                    </div>
                    <Link className="inline-block text-xs font-medium text-brand-navy hover:underline" to={`/support/customers/${memoModal.id}`}>
                      상세 페이지에서 더 보기 →
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {schedModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={schedBusy}
            onClick={() => { if (!schedBusy) setSchedModal(null) }}
            aria-label="일정 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-sched-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-sched-title" className="font-bold text-sm sm:text-base">
                  일정 등록 · {schedModal.name}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={schedBusy}
                  onClick={() => setSchedModal(null)}
                >
                  닫기
                </button>
              </div>
              <div className="p-4 sm:p-5 space-y-3 text-sm">
                <p className="text-xs text-slate-500">
                  내 개인 일정으로 등록되며, 이 고객과 연결됩니다.{' '}
                  <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">업무 캘린더</Link>에서 확인할 수 있습니다.
                </p>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">제목</label>
                  <input
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={schedTitle}
                    onChange={(e) => setSchedTitle(e.target.value)}
                    disabled={schedBusy}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">설명 (선택)</label>
                  <textarea
                    className="w-full min-h-[72px] rounded border px-3 py-2 text-sm"
                    value={schedDesc}
                    onChange={(e) => setSchedDesc(e.target.value)}
                    disabled={schedBusy}
                    placeholder="통화 목적, 준비물 등"
                  />
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">시작</label>
                    <input
                      type="datetime-local"
                      className="w-full rounded border px-2 py-2 text-sm"
                      value={schedStart}
                      onChange={(e) => setSchedStart(e.target.value)}
                      disabled={schedBusy}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-600 mb-0.5">종료</label>
                    <input
                      type="datetime-local"
                      className="w-full rounded border px-2 py-2 text-sm"
                      value={schedEnd}
                      onChange={(e) => setSchedEnd(e.target.value)}
                      disabled={schedBusy}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                    disabled={schedBusy}
                    onClick={() => setSchedModal(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                    disabled={schedBusy}
                    onClick={submitSchedule}
                  >
                    {schedBusy ? '등록 중…' : '일정 등록'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {contactModal ? (
        <div className="fixed inset-0 z-[59]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            disabled={contactBusy}
            onClick={() => { if (!contactBusy) setContactModal(null) }}
            aria-label="연락 닫기"
          />
          <div className="relative mx-auto max-w-lg px-3 py-10 sm:px-4 pointer-events-none">
            <div
              className="pointer-events-auto rounded-2xl bg-white shadow-xl border overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customer-contact-title"
            >
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3 sm:px-5">
                <div id="customer-contact-title" className="font-bold text-sm sm:text-base">
                  연락 등록 · {contactModal.name}
                </div>
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                  disabled={contactBusy}
                  onClick={() => setContactModal(null)}
                >
                  닫기
                </button>
              </div>
              <div className="p-4 sm:p-5 space-y-3 text-sm">
                <p className="text-xs text-slate-500">
                  연락 이력은 고객과 연결된 개인 일정으로 함께 저장되어 <Link className="font-medium text-brand-navy hover:underline" to="/work/calendar">일정 메뉴</Link>에서도 확인할 수 있습니다.
                </p>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 일시</label>
                  <input
                    type="datetime-local"
                    className="w-full rounded border px-2 py-2 text-sm"
                    value={contactAt}
                    onChange={(e) => setContactAt(e.target.value)}
                    disabled={contactBusy}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-0.5">연락 내용</label>
                  <textarea
                    className="w-full min-h-[120px] rounded border px-3 py-2 text-sm"
                    value={contactContent}
                    onChange={(e) => setContactContent(e.target.value)}
                    disabled={contactBusy}
                    maxLength={2000}
                    placeholder="통화/문자/방문 등 연락 내용과 후속 조치를 간략히 적어 주세요."
                  />
                  <div className="mt-1 text-right text-[11px] text-slate-400">{contactContent.length}/2000</div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                    disabled={contactBusy}
                    onClick={() => setContactModal(null)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                    disabled={contactBusy}
                    onClick={submitContact}
                  >
                    {contactBusy ? '등록 중…' : '연락 등록'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
